import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";

// Letter-by-letter reveal (Maiko "split text"). `base` delays the whole word in ms.
export const SplitText = ({ text = "", base = 0, className = "", as: Tag = "span", testId }) => {
  let i = 0;
  return (
    <Tag className={`s-split ${className}`} style={{ "--s-base": `${base}ms` }} data-testid={testId} aria-label={text}>
      {text.split(" ").map((word, w) => (
        <span key={w} className="inline-block whitespace-nowrap" aria-hidden="true">
          {[...word].map((ch, c) => (
            <span key={c} className="s-ch" style={{ "--i": i++ }}>
              {ch}
            </span>
          ))}
          {w < text.split(" ").length - 1 && <span className="s-ws" />}
        </span>
      ))}
    </Tag>
  );
};

// Continuous horizontal marquee; children are duplicated for the seamless loop.
export const Marquee = ({ children, speed = 40, className = "", testId }) => (
  <div className={`s-marquee-wrap overflow-hidden ${className}`} data-testid={testId}>
    <div className="s-marquee" style={{ "--s-marquee-speed": `${speed}s` }}>
      <div className="flex shrink-0 items-center">{children}</div>
      <div className="flex shrink-0 items-center" aria-hidden="true">
        {children}
      </div>
    </div>
  </div>
);

// Hairline that draws itself when scrolled into view.
export const Rule = ({ className = "" }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && el.classList.add("is-in")),
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <div ref={ref} className={`s-rule ${className}`} />;
};

// Number that counts up the first time it becomes visible.
export const CountUp = ({ value = 0, suffix = "", className = "", testId }) => {
  const ref = useRef(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    const el = ref.current;
    const target = Number(value) || 0;
    if (!el) return;
    let raf;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / 1400);
          setN(Math.round(target * (1 - Math.pow(1 - p, 3))));
          if (p < 1) raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value]);
  return (
    <span ref={ref} className={className} data-testid={testId}>
      {n.toLocaleString()}
      {suffix}
    </span>
  );
};

// Maiko-style text link with a long arrow that stretches on hover.
export const ArrowLink = ({ to, href, children, dark = false, className = "", testId, onClick }) => {
  const cls = `s-arrow-link ${dark ? "s-on-dark" : ""} ${className}`;
  const inner = (
    <>
      <span>{children}</span>
      <span className="s-arrow" />
    </>
  );
  if (href)
    return (
      <a href={href} className={cls} data-testid={testId} onClick={onClick} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">
        {inner}
      </a>
    );
  if (to)
    return (
      <Link to={to} className={cls} data-testid={testId} onClick={onClick}>
        {inner}
      </Link>
    );
  return (
    <button type="button" className={cls} data-testid={testId} onClick={onClick}>
      {inner}
    </button>
  );
};

// Horizontal row you can drag with the mouse; shows a round "DRAG" cursor label.
export const DragRow = ({ children, className = "", testId, label = "Drag", arrows = false }) => {
  const ref = useRef(null);
  const state = useRef({ down: false, x: 0, left: 0, moved: false });
  const [cursor, setCursor] = useState({ x: 0, y: 0, on: false });

  const nudge = (dir) => {
    const el = ref.current;
    if (el) el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 320), behavior: "smooth" });
  };
  const arrowBtn =
    "flex h-12 w-12 items-center justify-center bg-[color:var(--s-dark)] text-white transition-colors duration-300 hover:bg-[color:var(--s-gold-bright)] hover:text-[color:var(--s-ink)]";

  const down = (e) => {
    const el = ref.current;
    state.current = { down: true, x: e.clientX, left: el.scrollLeft, moved: false };
    el.classList.add("is-dragging");
  };
  const move = (e) => {
    setCursor({ x: e.clientX, y: e.clientY, on: true });
    if (!state.current.down) return;
    const dx = e.clientX - state.current.x;
    if (Math.abs(dx) > 4) state.current.moved = true;
    ref.current.scrollLeft = state.current.left - dx;
  };
  const up = () => {
    state.current.down = false;
    ref.current?.classList.remove("is-dragging");
  };
  const clickGuard = (e) => {
    if (state.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      state.current.moved = false;
    }
  };

  return (
    <div className="relative">
      <div
        ref={ref}
        data-testid={testId}
        className={`s-drag flex overflow-x-auto ${className}`}
        onMouseDown={down}
        onMouseMove={move}
        onMouseUp={up}
        onMouseLeave={() => {
          up();
          setCursor((c) => ({ ...c, on: false }));
        }}
        onClickCapture={clickGuard}
      >
        {children}
      </div>
      {arrows && (
        <div className="mt-6 flex gap-2 px-6 lg:px-12" data-testid={`${testId}-arrows`}>
          <button type="button" onClick={() => nudge(-1)} aria-label="Scroll left" data-testid={`${testId}-prev`} className={arrowBtn}>
            <CaretLeft size={18} weight="bold" />
          </button>
          <button type="button" onClick={() => nudge(1)} aria-label="Scroll right" data-testid={`${testId}-next`} className={arrowBtn}>
            <CaretRight size={18} weight="bold" />
          </button>
        </div>
      )}
      <span
        className="s-drag-label hidden lg:flex"
        style={{ left: cursor.x, top: cursor.y, "--s-drag-scale": cursor.on ? 1 : 0 }}
        aria-hidden="true"
      >
        {label}
      </span>
    </div>
  );
};
